"use client";

import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useContext,
} from "react";

export const ThemeContext = React.createContext({
  siteLoaded: false,
  touch: true,
  touchFired: false,
  isTransitioning: false,
  setSiteLoaded: () => {},
  setTouch: () => {},
  setTouchFired: () => {},
  setIsTransitioning: () => {},
});

export const ThemeContextProvider = ({ children }) => {
  const [siteLoaded, setSiteLoadedState] = useState(false);
  const [touch, setTouch] = useState(true);
  const [touchFired, setTouchFiredState] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const suppressAnimation = useRef(false);

  const setSiteLoaded = useCallback(() => setSiteLoadedState(true), []);
  const setTouchFired = useCallback(() => {
    setTouch(true);
    setTouchFiredState(true);
  }, []);

  // Enable video autoplay after first touch
  useEffect(() => {
    if (touchFired) {
      document.querySelectorAll("video").forEach((video) => {
        if (video.paused && video.muted) {
          video.play().catch(() => {
            // Silently handle any errors
          });
        }
      });
    }
  }, [touchFired]);

  const value = useMemo(
    () => ({
      siteLoaded,
      touch,
      touchFired,
      isTransitioning,
      setSiteLoaded,
      setTouch,
      setTouchFired,
      setIsTransitioning,
    }),
    [siteLoaded, touch, touchFired, isTransitioning, setSiteLoaded, setTouchFired]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Separate context for viewport dimensions. Kept apart from ThemeContext so
// that components reading e.g. `touch` or `siteLoaded` don't re-render on
// every resize — same rationale as the DesktopContext split below.
export const DimensionsContext = React.createContext({
  dimensions: { screenWidth: 0, screenHeight: 0, bodyHeight: 0 },
  setDimensions: () => {},
});

export const DimensionsContextProvider = ({ children }) => {
  const [dimensions, setDimensions] = useState({
    screenWidth: 0,
    screenHeight: 0,
    bodyHeight: 0,
  });

  const value = useMemo(
    () => ({ dimensions, setDimensions }),
    [dimensions]
  );

  return (
    <DimensionsContext.Provider value={value}>
      {children}
    </DimensionsContext.Provider>
  );
};

// Convenience hook for the common case of just reading dimensions.
export const useDimensions = () => useContext(DimensionsContext).dimensions;

// Separate context for the desktop breakpoint. Kept apart from ThemeContext so
// that components reading `isDesktop` don't re-render when `dimensions` (which
// changes on every resize) updates. The matchMedia listener only fires when the
// 1024px breakpoint is actually crossed.
export const DesktopContext = React.createContext(false);

export const DesktopContextProvider = ({ children }) => {
  // MUST seed `false` to match the server render. This site is statically
  // generated, so the server has no viewport and always renders the mobile
  // branch. Seeding from `window.matchMedia` here made the first *client*
  // render disagree with the server HTML on desktop, causing hydration
  // mismatches (React #418/#423). Any render output that must be correct on
  // first paint (e.g. icon stroke width) belongs in a CSS media query, not
  // here — this flag only flips after mount.
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);

    update();
    mq.addEventListener("change", update);

    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <DesktopContext.Provider value={isDesktop}>
      {children}
    </DesktopContext.Provider>
  );
};

// Convenience hook so consumers don't need to import useContext + DesktopContext.
export const useIsDesktop = () => useContext(DesktopContext);
